import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { styled, Theme } from '@mui/material/styles';
import { DropResult } from 'react-beautiful-dnd';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import Avatar from '@mui/material/Avatar';
import DescriptionIcon from '@mui/icons-material/Description';
import AddIcon from '@mui/icons-material/Add';
import SortIcon from '@mui/icons-material/Sort';
import SaveIcon from '@mui/icons-material/Save';
import UndoIcon from '@mui/icons-material/Undo';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import {
  IconButton,
  Toolbar,
  ListItemSecondaryAction,
  Typography,
  Tooltip,
} from '@mui/material';

import DigitalCaseAddEditDialog, { DigitalCaseEdit } from './DigitalCaseAddEditDialog';
import Prompt from '../layout/Prompt';

import { DigitalCase } from '../../redux/types';
import usePrevious from '../../hooks/usePrevious';
import SortableList from '../layout/SortableList';
import ApiClient from '../../loopback/apiClient';
import { getLastSortIndex } from '../../utilities/arrayHelper';

const PREFIX = 'CaseList';

const classes = {
  root: `${PREFIX}-root`,
  listContainer: `${PREFIX}-listContainer`,
  listToolbar: `${PREFIX}-listToolbar`,
};

// TODO jss-to-styled codemod: The Fragment root was replaced by div. Change the tag if needed.
const Root = styled('div')((
  { theme }: { theme: Theme },
) => ({
  [`& .${classes.root}`]: {
    width: '100%',
    backgroundColor: theme.palette.background.paper,
  },

  [`& .${classes.listContainer}`]: {
    position: 'absolute',
    top: 65,
    bottom: 0,
    overflow: 'auto',
  },

  [`& .${classes.listToolbar}`]: {
    backgroundColor: theme.palette.grey[100],
  },
}));

type Props = {
  caseList?: DigitalCase[];
  selectedCase?: DigitalCase;
  setDigitalCases: (dc: DigitalCase[]) => void;
  setSelectedCase: (selectedCaseId?: number) => void;
  digitalSectionId?: number;
  digitalSurveyId: number;
  addCase: (dgtlCase: DigitalCaseEdit) => void;
  updateCase: (dCase: DigitalCase) => void;
  deleteCase: (caseId: number) => void;
  showErrorNotification: (val: string) => void;
  submissionExist?: boolean;
  isEditorPrivileged?: boolean;
};

const CaseList: React.FC<Props> = ({
  caseList,
  selectedCase,
  setDigitalCases,
  setSelectedCase,
  digitalSectionId,
  digitalSurveyId,
  addCase,
  updateCase,
  deleteCase,
  showErrorNotification,
  submissionExist,
  isEditorPrivileged,
}: Props) => {
  const prevDigitalSectionId = usePrevious(digitalSectionId);
  const [showActions, setShowActions] = useState<number>();
  const [sortMode, setSortMode] = useState<boolean>(false);
  const [showCaseAddEditDialog, setShowCaseAddEditDialog] = useState<boolean>(false);
  const [caseBuffer, setCaseBuffer] = useState<DigitalCase>();
  const [promptDelete, setPromptDelete] = useState<boolean>(false);
  const sortedListRef = useRef<DigitalCase[]>();

  useEffect(() => {
    if (prevDigitalSectionId !== digitalSectionId) {
      if (caseList && caseList.length) {
        setSelectedCase(caseList[0].id);
      } else {
        setSelectedCase(undefined);
      }
    }
  }, [digitalSectionId, setSelectedCase, caseList, prevDigitalSectionId]);

  useEffect(() => {
    if (caseList && caseList.length && digitalSectionId && !selectedCase) {
      setSelectedCase(caseList[0].id);
    }
  }, [caseList, digitalSectionId, selectedCase, setSelectedCase]);

  /** Update in redux and backend on save */
  const onSaveSortOrder = () => {
    const sortedList = sortedListRef.current;
    if (!sortedList) {
      return;
    }
    const updatedList = sortedList.map((i, index) => {
      const updatedSortIndex: DigitalCase = { ...i, sortIndex: index };
      return ApiClient.updateDigitalCase(updatedSortIndex.id, updatedSortIndex);
    });
    Promise.all(updatedList).then((uList) => {
      setDigitalCases(uList);
    }).catch((e) => {
      showErrorNotification(JSON.stringify(e));
    }).finally(() => {
      setSortMode(false);
    });
  };

  const onDragHandler = useCallback((result: DropResult, sortedList?: DigitalCase[]) => {
    if (sortedList) {
      sortedListRef.current = sortedList;
    }
  }, []);

  const onAddCase = () => {
    setCaseBuffer(undefined);
    setShowCaseAddEditDialog(true);
  };

  const onEditCase = (caseId: number) => {
    const caseToEditTemp = caseList?.find((c) => c.id === caseId);
    setCaseBuffer(caseToEditTemp);
    setShowCaseAddEditDialog(true);
  };

  const onCloseAddEditDialog = () => {
    setCaseBuffer(undefined);
    setShowCaseAddEditDialog(false);
  };

  const onDeleteCase = (caseId: number) => {
    const caseToDeleteTemp = caseList?.find((c) => c.id === caseId);
    setCaseBuffer(caseToDeleteTemp);
    setPromptDelete(true);
  };

  const confirmDeleteCaseHandler = async (confirm: boolean) => {
    if (!caseList || !caseBuffer) {
      return;
    }
    if (!confirm) {
      setPromptDelete(false);
      setCaseBuffer(undefined);
      return;
    }
    if (submissionExist) {
      showErrorNotification('Cannot delete case. Submissions exist for this trial.');
      return;
    }
    deleteCase(caseBuffer.id);
    setPromptDelete(false);
    setCaseBuffer(undefined);
  };

  const addEditCaseHandler = async (caseData: DigitalCaseEdit) => {
    if (submissionExist && !isEditorPrivileged) {
      showErrorNotification('Cannot edit or add case. Submissions exist for this trial.');
      return;
    }
    if (caseData.id !== undefined) {
      updateCase(caseData as DigitalCase);
    } else {
      const sortIdx = getLastSortIndex<DigitalCase>(caseList);
      addCase({ ...caseData, sortIndex: sortIdx });
    }
    onCloseAddEditDialog();
  };

  if (!digitalSectionId) {
    return null;
  }
  return (
    (
      <Root>
        <div style={{ minHeight: '250px' }}>
          <Toolbar className={classes.listToolbar}>
            <Typography variant="body1" style={{ flexGrow: 1 }}>Case List</Typography>
            {
              !sortMode && (
                <Tooltip title="Reorder" aria-label="sort" placement="top">
                  <IconButton onClick={() => setSortMode(true)} size="large">
                    <SortIcon />
                  </IconButton>
                </Tooltip>
              )
            }
            {
              !sortMode && (
                <Tooltip title="Add" aria-label="add" placement="top">
                  <IconButton disabled={submissionExist} onClick={onAddCase} size="large">
                    <AddIcon />
                  </IconButton>
                </Tooltip>
              )
            }
            {
              sortMode && (
                <Tooltip title="Undo" aria-label="undo" placement="top">
                  <IconButton onClick={() => setSortMode(false)} size="large">
                    <UndoIcon />
                  </IconButton>
                </Tooltip>
              )
            }
            {
              sortMode && (
                <Tooltip title="Save Order" aria-label="save order" placement="top">
                  <IconButton onClick={onSaveSortOrder} size="large">
                    <SaveIcon />
                  </IconButton>
                </Tooltip>
              )
            }
          </Toolbar>
          <List className={`${classes.root} ${classes.listContainer}`}>
            {
              caseList && selectedCase && !sortMode
              && caseList.map((c: DigitalCase) => (
                <ListItem
                  key={c.id}
                  button
                  divider
                  selected={c.id === selectedCase.id}
                  onClick={() => { setSelectedCase(c.id); }}
                  onMouseOver={() => { setShowActions(c.id); }}
                  onMouseLeave={() => { setShowActions(undefined); }}
                >
                  <ListItemAvatar>
                    <Avatar>
                      <DescriptionIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText primary={c.name} />
                  {
                    showActions === c.id
                    && (
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          aria-label="comments"
                          onClick={() => { onEditCase(c.id); }}
                          size="large"
                        >
                          <EditOutlinedIcon />
                        </IconButton>
                        <IconButton
                          edge="end"
                          aria-label="comments"
                          onClick={() => { onDeleteCase(c.id); }}
                          size="large"
                        >
                          <DeleteOutlinedIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    )
                  }
                </ListItem>
              ))
            }
            {
              caseList && sortMode && (
                <SortableList<DigitalCase>
                  list={caseList}
                  droppableId="caseOrganizer"
                  onDragEndHandler={onDragHandler}
                />
              )
            }
          </List>
        </div>
        <DigitalCaseAddEditDialog
          display={showCaseAddEditDialog}
          onClose={onCloseAddEditDialog}
          onSubmit={addEditCaseHandler}
          caseData={caseBuffer}
          digitalSectionId={digitalSectionId}
          digitalSurveyId={digitalSurveyId}
        />
        <Prompt
          display={promptDelete}
          title="Delete Case"
          action={confirmDeleteCaseHandler}
          maxWidth="sm"
          color="tertiary"
          language="en"
          version={2}
        >
          Are you sure you want to delete this case?
        </Prompt>
      </Root>
    )
  );
};

export default CaseList;
